import { OffboardingInitiatedBy } from "@/modules/offboarding/enums"
import { useEffect, useRef, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import {
  Briefcase, Calendar, Mail, MessageSquare, User,
  CheckCircle, XCircle, Download, Loader2, Upload, Pencil, Save, X,
  Clock, AlertCircle,
} from "lucide-react"
import { toast } from "sonner"
import { Spinner } from "@/modules/shared/ui/components/spinner"
import { Badge } from "@/modules/shared/ui/components/badge"
import { Card, CardContent } from "@/modules/shared/ui/components/card"
import { Button } from "@/modules/shared/ui/components/button"
import { Input } from "@/modules/shared/ui/components/input"
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/modules/shared/ui/components/dialog"
import type { AppDispatch } from "@/store"
import { selectOnboardingData, selectOnboardingIsLoading, fetchEmployeeOnboardingThunk } from "@/modules/employee/employee/store/employeeDetailsSlice"
import {
  fetchEmployeeOffboardingThunk,
  employeeInitiateOffboardingThunk,
  selectEmployeeOffboarding,
} from "@/modules/offboarding/store/employeeOffboardingSlice"
import { offboardingService } from "@/modules/offboarding/infrastructure/offboarding-service"
import { OffboardingStatus, isOffboardingStatus, isOffboardingTerminal } from "@/modules/offboarding/enums"

const STATUS_CLASSES: Record<string, string> = {
  pending:  "bg-amber-500/10 text-amber-600 dark:text-amber-400 border-amber-500/20",
  accepted: "bg-green-500/10 text-green-600 dark:text-green-400 border-green-500/20",
  rejected: "bg-destructive/10 text-destructive border-destructive/20",
}

function InfoRow({ icon, label, value }: { icon: React.ReactNode; label: string; value: React.ReactNode }) {
  return (
    <div className="flex items-start gap-3 py-3 border-b border-border last:border-0">
      <div className="text-muted-foreground mt-0.5 shrink-0">{icon}</div>
      <div className="flex-1 min-w-0">
        <p className="text-xs text-muted-foreground font-medium uppercase tracking-wide">{label}</p>
        <div className="text-sm text-foreground mt-0.5">{value}</div>
      </div>
    </div>
  )
}

function StartOffboardingDialog({
  open, onOpenChange, onSuccess,
}: { open: boolean; onOpenChange: (v: boolean) => void; onSuccess: () => void }) {
  const dispatch = useDispatch<AppDispatch>()
  const onboardingData = useSelector(selectOnboardingData)
  const { initiating } = useSelector(selectEmployeeOffboarding)
  const [reason, setReason] = useState("")
  const [resignationLetter, setResignationLetter] = useState<File | null>(null)
  const fileRef = useRef<HTMLInputElement | null>(null)

  const handleSubmit = async () => {
    if (!reason.trim() || !resignationLetter || !onboardingData?.employeeId) {
      toast.error("Please fill in all required fields")
      return
    }
    try {
      await dispatch(employeeInitiateOffboardingThunk({
        employeeId: onboardingData.employeeId,
        offboardingReason: reason.trim(),
        resignationLetter,
      })).unwrap()
      toast.success("Offboarding started successfully")
      onOpenChange(false)
      onSuccess()
    } catch (err) {
      toast.error(typeof err === "string" ? err : "Failed to start offboarding")
    }
  }

  const handleClose = () => {
    if (initiating) return
    setReason(""); setResignationLetter(null)
    onOpenChange(false)
  }

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) handleClose() }}>
      <DialogContent className="w-full max-w-md p-0 bg-card">
        <div className="p-6 border-b border-border">
          <DialogHeader className="gap-1">
            <DialogTitle className="text-xl font-bold">Start Offboarding</DialogTitle>
            <DialogDescription className="text-sm text-muted-foreground">
              Submit your resignation and begin the offboarding process.
            </DialogDescription>
          </DialogHeader>
        </div>
        <div className="p-6 space-y-4">
          <div>
            <label className="text-sm font-medium text-foreground">Reason <span className="text-destructive">*</span></label>
            <Input className="mt-1" placeholder="e.g. Personal reasons, New opportunity" value={reason} onChange={(e) => setReason(e.target.value)} />
          </div>
          <div>
            <label className="text-sm font-medium text-foreground">Resignation Letter <span className="text-destructive">*</span></label>
            <div className="mt-1 flex items-center gap-2">
              <input type="file" aria-label="Upload resignation letter" className="hidden" ref={fileRef} accept=".pdf,.doc,.docx" onChange={(e: React.ChangeEvent<HTMLInputElement>) => { setResignationLetter(e.target.files?.[0] ?? null); e.target.value = "" }} />
              <Button type="button" variant="outline" size="sm" className="text-xs" onClick={() => fileRef.current?.click()}>
                <Upload className="w-3 h-3 mr-1" />{resignationLetter ? resignationLetter.name : "Choose file"}
              </Button>
              {resignationLetter && (
                <Button type="button" onClick={() => setResignationLetter(null)} className="text-muted-foreground hover:text-destructive">
                  <X className="w-4 h-4" />
                </Button>
              )}
            </div>
          </div>
        </div>
        <div className="p-4 border-t border-border flex justify-end gap-3">
          <Button variant="outline" onClick={handleClose} disabled={initiating}>Cancel</Button>
          <Button className="bg-primary hover:bg-primary/90 text-primary-foreground" onClick={handleSubmit} disabled={initiating || !reason.trim() || !resignationLetter}>
            {initiating ? <><Loader2 className="w-4 h-4 animate-spin mr-2" />Submitting...</> : "Submit"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}

export function EmployeeOffboardingPage() {
  const dispatch = useDispatch<AppDispatch>()
  const onboardingData = useSelector(selectOnboardingData)
  const onboardingLoading = useSelector(selectOnboardingIsLoading)
  const { data: offboarding, loading, notFound } = useSelector(selectEmployeeOffboarding)
  const [dialogOpen, setDialogOpen] = useState(false)

  // Reason editing
  const [isEditingReason, setIsEditingReason] = useState(false)
  const [editReason, setEditReason] = useState("")
  const [isSavingReason, setIsSavingReason] = useState(false)

  // Document uploads: docId -> { file, uploading }
  const [uploads, setUploads] = useState<Record<string, { file: File | null; uploading: boolean }>>({})
  const fileInputRefs = useRef<Record<string, HTMLInputElement | null>>({})

  useEffect(() => { dispatch(fetchEmployeeOnboardingThunk()) }, [dispatch])

  useEffect(() => {
    const id = onboardingData?.employeeId
    if (id && id !== "00000000-0000-0000-0000-000000000000") {
      dispatch(fetchEmployeeOffboardingThunk(id))
    }
  }, [dispatch, onboardingData?.employeeId])

  useEffect(() => {
    if (offboarding) setEditReason(offboarding.offboardingReason)
  }, [offboarding])

  const refresh = () => {
    const id = onboardingData?.employeeId
    if (id && id !== "00000000-0000-0000-0000-000000000000") dispatch(fetchEmployeeOffboardingThunk(id))
  }

  const handleSaveReason = async () => {
    if (!offboarding || !onboardingData?.organizationId || !editReason.trim()) return
    setIsSavingReason(true)
    try {
      await offboardingService.patch(onboardingData.organizationId, offboarding.id, { offboardingReason: editReason.trim() })
      toast.success("Reason updated")
      setIsEditingReason(false)
      refresh()
    } catch { toast.error("Failed to update reason") }
    finally { setIsSavingReason(false) }
  }

  const handleUpload = async (docId: string) => {
    const entry = uploads[docId]
    if (!entry?.file || !offboarding || !onboardingData?.organizationId) return
    setUploads((p) => ({ ...p, [docId]: { ...p[docId], uploading: true } }))
    try {
      await offboardingService.uploadDocument(
        onboardingData.organizationId,
        offboarding.id,
        entry.file,
        { existingDocumentId: docId },
      )
      toast.success("Document uploaded")
      setUploads((p) => { const n = { ...p }; delete n[docId]; return n })
      refresh()
    } catch {
      toast.error("Failed to upload document")
      setUploads((p) => ({ ...p, [docId]: { ...p[docId], uploading: false } }))
    }
  }

  const handleDownload = async (docId: string, fileName: string) => {
    if (!offboarding || !onboardingData?.organizationId) return
    try {
      const { sasUrl } = await offboardingService.downloadDocument(onboardingData.organizationId, offboarding.id, docId)
      const a = document.createElement("a")
      a.href = sasUrl; a.download = fileName; a.target = "_blank"; a.rel = "noopener noreferrer"
      document.body.appendChild(a); a.click(); document.body.removeChild(a)
    } catch { toast.error("Failed to download document") }
  }

  const handleView = async (docId: string) => {
    if (!offboarding || !onboardingData?.organizationId) return
    try {
      const { sasUrl } = await offboardingService.downloadDocument(onboardingData.organizationId, offboarding.id, docId)
      window.open(sasUrl, "_blank", "noopener,noreferrer")
    } catch { toast.error("Failed to open document") }
  }

  if (loading || onboardingLoading) return <div className="flex items-center justify-center py-24"><Spinner className="size-6" /></div>

  if (notFound || !offboarding) {
    const canStartOffboarding = onboardingData?.employeeStatus?.toLowerCase() === "active"
    return (
    <div className="min-h-screen bg-background flex items-center justify-center">
      <div className="text-center">
        <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center mx-auto mb-4">
          <Briefcase className="w-8 h-8 text-muted-foreground" />
        </div>
        <h2 className="text-lg font-semibold text-foreground">No Offboarding Started</h2>
        <p className="text-sm text-muted-foreground mt-1 mb-6">
          {canStartOffboarding ? "You don't have an active offboarding process." : "Offboarding is not available until your onboarding is complete."}
        </p>
        {canStartOffboarding && (
          <Button className="bg-primary hover:bg-primary/90 text-primary-foreground" onClick={() => setDialogOpen(true)}>
            Start Offboarding
          </Button>
        )}
      </div>
      <StartOffboardingDialog open={dialogOpen} onOpenChange={setDialogOpen} onSuccess={refresh} />
    </div>
    )
  }

  const statusCls = STATUS_CLASSES[offboarding.resignationStatus?.toLowerCase() ?? ""] ?? "bg-muted text-muted-foreground border-border"
  const documents = offboarding.documents ?? []
  const submittedCount = documents.filter((d) => d.submitted).length
  const isTerminal = isOffboardingTerminal(offboarding.resignationStatus)
  const isPending = isOffboardingStatus(offboarding.resignationStatus, OffboardingStatus.Pending)
  const isAccepted = isOffboardingStatus(offboarding.resignationStatus, OffboardingStatus.Accepted)
  const isRejected = isOffboardingStatus(offboarding.resignationStatus, OffboardingStatus.Rejected)
  const terminationDate = offboarding.terminationEligibleAt ? new Date(offboarding.terminationEligibleAt) : null
  const daysRemaining = terminationDate
    ? Math.ceil((terminationDate.getTime() - Date.now()) / (24 * 60 * 60 * 1000))
    : null

  return (
    <div className="min-h-screen bg-background">
      <main className="max-w-4xl mx-auto px-6 py-10 space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Offboarding</h1>
          <p className="text-sm text-muted-foreground mt-1">Your offboarding details and required documents.</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-6">

            {/* Details */}
            <Card className="bg-card border border-border">
              <CardContent className="p-6">
                <h2 className="text-base font-semibold text-foreground mb-4">Offboarding Details</h2>

                {isPending && (
                  <div className="mb-4 flex items-start gap-3 rounded-md border border-blue-200 bg-blue-50 dark:border-blue-500/20 dark:bg-blue-500/10 p-3 text-sm text-blue-800 dark:text-blue-300">
                    <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
                    <span>
                      Your offboarding request is being reviewed by HR. Once it's accepted, you'll be given a
                      grace period during which you'll keep full access to the platform — after that, your
                      account access will end automatically.
                    </span>
                  </div>
                )}

                {isAccepted && (
                  <div className="mb-4 flex items-start gap-3 rounded-md border border-emerald-200 bg-emerald-50 dark:border-emerald-500/20 dark:bg-emerald-500/10 p-3 text-sm text-emerald-800 dark:text-emerald-300">
                    <Clock className="w-4 h-4 mt-0.5 shrink-0" />
                    <span>
                      Your offboarding has been accepted
                      {offboarding.gracePeriodDays != null ? ` with a ${offboarding.gracePeriodDays}-day grace period` : ""}.
                      You'll keep access to the platform
                      {terminationDate && daysRemaining !== null && daysRemaining > 0
                        ? ` for ${daysRemaining} more day${daysRemaining === 1 ? "" : "s"} — until ${terminationDate.toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" })}`
                        : ""}
                      . After that, your account will be deactivated automatically.
                    </span>
                  </div>
                )}

                {isRejected && (
                  <div className="mb-4 rounded-md border border-destructive/20 bg-destructive/10 p-3 text-sm text-destructive">
                    Your resignation was rejected{offboarding.comments ? ` — "${offboarding.comments}"` : ""}. Contact HR if you have questions or still wish to leave.
                  </div>
                )}

                {/* Editable reason */}
                <div className="flex items-start gap-3 py-3 border-b border-border">
                  <div className="text-muted-foreground mt-0.5 shrink-0"><User className="w-4 h-4" /></div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs text-muted-foreground font-medium uppercase tracking-wide">Reason</p>
                    {!isEditingReason ? (
                      <div className="flex items-center justify-between mt-0.5">
                        <span className="text-sm text-foreground">{offboarding.offboardingReason}</span>
                        {!isTerminal && (
                          <Button size="sm" variant="outline" className="h-6 text-xs px-2" onClick={() => setIsEditingReason(true)}>
                            <Pencil className="w-3 h-3 mr-1" />Edit
                          </Button>
                        )}
                      </div>
                    ) : (
                      <div className="mt-1 flex items-center gap-2">
                        <Input className="h-8 text-sm flex-1" value={editReason} onChange={(e) => setEditReason(e.target.value)} />
                        <Button size="sm" variant="outline" className="h-8" onClick={() => setIsEditingReason(false)} disabled={isSavingReason}>
                          <X className="w-3.5 h-3.5" />
                        </Button>
                        <Button size="sm" className="h-8 bg-primary hover:bg-primary/90 text-primary-foreground" onClick={handleSaveReason} disabled={isSavingReason || !editReason.trim()}>
                          {isSavingReason ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
                        </Button>
                      </div>
                    )}
                  </div>
                </div>

                <InfoRow icon={<Calendar className="w-4 h-4" />} label="Last Working Date"
                  value={new Date(offboarding.lastWorkingDate).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" })} />
                <InfoRow icon={<Mail className="w-4 h-4" />} label="Email" value={offboarding.employeeEmail} />
                <InfoRow icon={<Briefcase className="w-4 h-4" />} label="Position" value={offboarding.employeePosition} />
                <InfoRow icon={<User className="w-4 h-4" />} label="Initiated By"
                  value={<Badge className={offboarding.initiatedBy?.toLowerCase() === OffboardingInitiatedBy.Admin.toLowerCase() ? "bg-purple-500/10 text-purple-600 dark:text-purple-400 border-purple-500/20" : "bg-primary/10 text-primary border-primary/20"}>{offboarding.initiatedBy}</Badge>} />
                {offboarding.comments && (
                  <InfoRow icon={<MessageSquare className="w-4 h-4" />} label="Comments"
                    value={<span className="italic text-muted-foreground">"{offboarding.comments}"</span>} />
                )}
              </CardContent>
            </Card>

            {/* Documents */}
            <Card className="bg-card border border-border">
              <CardContent className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-base font-semibold text-foreground">Required Documents</h2>
                  <span className="text-xs text-muted-foreground">{submittedCount}/{documents.length} submitted</span>
                </div>
                {documents.length === 0 ? (
                  <p className="text-sm text-muted-foreground">No documents required.</p>
                ) : (
                  <div className="space-y-3">
                    {documents.map((doc) => {
                      const entry = uploads[doc.id]
                      return (
                        <div key={doc.id} className="rounded-md border border-border bg-muted p-3 space-y-2">
                          <div className="flex items-center gap-2">
                            {doc.submitted
                              ? <CheckCircle className="w-4 h-4 text-emerald-500 shrink-0" />
                              : <XCircle className="w-4 h-4 text-muted-foreground/40 shrink-0" />}
                            <span className="text-sm font-medium text-foreground flex-1 truncate">{doc.documentName}</span>
                            <Badge className={doc.employeeEditable ? "bg-primary/10 text-primary border-primary/20" : "bg-muted text-muted-foreground border-border"}>
                              {doc.employeeEditable ? "Editable" : "Read-only"}
                            </Badge>
                            {doc.documentUrl && (
                              <div className="flex items-center gap-2">
                                <Button type="button" onClick={() => handleView(doc.id)} className="text-xs text-primary hover:underline shrink-0" variant="link">
                                  View
                                </Button>
                                <Button type="button" onClick={() => handleDownload(doc.id, doc.documentName)} className="text-muted-foreground hover:text-foreground shrink-0" variant="link">
                                  <Download className="w-4 h-4" />
                                </Button>
                              </div>
                            )}
                          </div>
                          <p className="text-xs text-muted-foreground pl-6">
                            {doc.submitted ? "Submitted" : "Pending submission"} · Added {new Date(doc.createdAt).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}
                          </p>
                          {doc.employeeEditable && !isRejected && (
                            <div className="pl-6 flex items-center gap-2">
                              <input type="file" aria-label={`Upload ${doc.documentName}`} className="hidden" ref={(el) => { fileInputRefs.current[doc.id] = el }}
                                onChange={(e: React.ChangeEvent<HTMLInputElement>) => { const f = e.target.files?.[0]; if (f) setUploads((p) => ({ ...p, [doc.id]: { file: f, uploading: false } })); e.target.value = "" }} />
                              <Button type="button" variant="outline" size="sm" className="text-xs h-7" onClick={() => fileInputRefs.current[doc.id]?.click()}>
                                <Upload className="w-3 h-3 mr-1" />{entry?.file ? entry.file.name : "Choose file"}
                              </Button>
                              {entry?.file && (
                                <Button type="button" size="sm" className="h-7 text-xs bg-primary hover:bg-primary/90 text-primary-foreground" disabled={entry.uploading} onClick={() => handleUpload(doc.id)}>
                                  {entry.uploading ? <Loader2 className="w-3 h-3 animate-spin" /> : "Upload"}
                                </Button>
                              )}
                            </div>
                          )}
                        </div>
                      )
                    })}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Status */}
          <div className="space-y-6">
            <Card className="bg-card border border-border">
              <CardContent className="p-6">
                <h2 className="text-base font-semibold text-foreground mb-4">Status</h2>
                <Badge className={statusCls}>{offboarding.resignationStatus}</Badge>
                <div className="mt-4 pt-4 border-t border-border space-y-2 text-sm text-muted-foreground">
                  <p>Created: {new Date(offboarding.createdAt).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}</p>
                  <p>Updated: {new Date(offboarding.updatedAt).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}</p>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </main>
    </div>
  )
}
